import type { CriticalMomentKind } from "@chessla/shared";
import type { ChessSquare as Square } from "@/shared/components/chessboard";
import { hasUsableEvaluation } from "@/shared/components/evaluation";

export interface ReviewDecisionPosition {
  factId: string;
  fen: string;
  playedSan: string;
  playedFrom: Square | null;
  playedTo: Square | null;
  kind: CriticalMomentKind;
}

interface DecisionFact {
  ply?: number;
  factId: string;
  fenBefore: string;
  playedSan: string;
  kind: CriticalMomentKind;
}

interface PlayedEvidence {
  moveNumber?: number;
  fenBefore?: string;
  san: string;
  playedFrom?: string | null;
  playedTo?: string | null;
}

export const recordedMovePly = (move: Pick<PlayedEvidence, "moveNumber" | "fenBefore">): number | null => {
  if (!move.moveNumber || !move.fenBefore) return null;
  const side = move.fenBefore.split(" ")[1];
  if (side !== "w" && side !== "b") return null;
  return (move.moveNumber - 1) * 2 + (side === "w" ? 1 : 2);
};

export const matchesReviewDecision = (
  decision: ReviewDecisionPosition | null,
  move: PlayedEvidence | null,
): boolean => {
  if (!decision || move?.fenBefore !== decision.fen || move.san !== decision.playedSan) return false;
  const expectedPly = Number(decision.factId.match(/:ply:(\d+)$/)?.[1]);
  return !Number.isInteger(expectedPly) || recordedMovePly(move) === expectedPly;
};

export const shouldRunLiveReviewEvaluation = ({
  revealEvidence,
  fen,
  storedCp,
  storedMate,
}: {
  revealEvidence: boolean;
  fen: string;
  storedCp: number | null;
  storedMate: number | null;
}): boolean => revealEvidence && fen !== "start" && !hasUsableEvaluation({ cp: storedCp, mate: storedMate });

const square = (value: string | null | undefined): Square | null =>
  typeof value === "string" && /^[a-h][1-8]$/.test(value) ? (value as Square) : null;

export const reviewDecisionPosition = (moment: DecisionFact, move: PlayedEvidence | null): ReviewDecisionPosition => {
  const expectedPly = moment.ply ?? Number(moment.factId.match(/:ply:(\d+)$/)?.[1]);
  const matches =
    move?.san === moment.playedSan && move.fenBefore === moment.fenBefore && recordedMovePly(move) === expectedPly;
  return {
    factId: moment.factId,
    fen: moment.fenBefore,
    playedSan: moment.playedSan,
    playedFrom: matches ? square(move.playedFrom) : null,
    playedTo: matches ? square(move.playedTo) : null,
    kind: moment.kind,
  };
};

export const isReviewedPlayerTurn = (fen: string, playerColor: "w" | "b"): boolean =>
  fen.trim().split(/\s+/u)[1] === playerColor;

export const canExploreReviewDecision = (decision: ReviewDecisionPosition, playerColor: "w" | "b"): boolean =>
  decision.kind !== "opponent_miss" && isReviewedPlayerTurn(decision.fen, playerColor);
